import React, { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { RouteNotFound } from './common/components/route/RouteNotFound';
import { Account } from './features/account/Account';
import { AccountDashboard } from './features/account/AccountDashboard';
import { HardwareConfiguration } from './features/account/hardware/HardwareConfiguration';
import { HardwareConnection } from './features/account/hardware/HardwareConnection';
import { HardwareDetails } from './features/account/hardware/HardwareDetails';
import { HardwareList } from './features/account/hardware/HardwareList';
import { FamilyMembers } from './features/account/settings/family-members/FamilyMembers';
import { LicensesDetail } from './features/account/settings/licenses/LicensesDetail';
import { LicensesPackagesList } from './features/account/settings/licenses/LicensesPackagesList';
import { LicensesPublishersList } from './features/account/settings/licenses/LicensesPublishersList';
import { DataArchive } from './features/account/settings/privacy/data-archive/DataArchive';
import { DeleteAccountArchive } from './features/account/settings/privacy/delete-account/DeleteAccountArchive';
import { DeleteAccountConfirm } from './features/account/settings/privacy/delete-account/DeleteAccountConfirm';
import { DeleteAccountStart } from './features/account/settings/privacy/delete-account/DeleteAccountStart';
import { DeleteAccountVerify } from './features/account/settings/privacy/delete-account/DeleteAccountVerify';
import { PrivacySettingsHome } from './features/account/settings/privacy/PrivacySettingsHome';
import { SettingsDisplay } from './features/account/settings/SettingsDisplay';
import { SettingsHome } from './features/account/settings/SettingsHome';
import { SettingsLanguage } from './features/account/settings/SettingsLanguage';
import { Support } from './features/account/Support';
import { Login } from './features/auth/Login';
import { Logout } from './features/auth/logout/Logout';
import { UpdateRequired } from './features/auth/UpdateRequired';
import { ChargingActivity } from './features/ev-charger/activity/ChargingActivity';
import { DeviceDetails } from './features/ev-charger/dashboard/DeviceDetails';
import { DeviceStatus } from './features/ev-charger/dashboard/DeviceStatus';
import { EVChargerHome } from './features/ev-charger/dashboard/EVChargerHome';
import { NoDevice } from './features/ev-charger/dashboard/NoDevice';
import { DevicePowerUpInstructions } from './features/ev-charger/device-setup/DevicePowerUpInstructions';
import { DeviceRegister } from './features/ev-charger/device-setup/DeviceRegister';
import { DeviceScan } from './features/ev-charger/device-setup/DeviceScan';
import { UnplugEV } from './features/ev-charger/device-setup/UnplugEV';
import { WaitForYellowLED } from './features/ev-charger/device-setup/WaitForYellowLED';
import { Wifi24GhzInfo } from './features/ev-charger/device-setup/Wifi24GhzInfo';
import { WifiSetupFail } from './features/ev-charger/device-setup/WifiSetupFail';
import { DeviceChargingSchedule } from './features/ev-charger/schedules/DeviceChargingSchedule';
import { DeviceWifiConfiguration } from './features/ev-charger/wifi-configuration/DeviceWifiConfiguration';
import { DeviceWifiConnection } from './features/ev-charger/wifi-configuration/DeviceWifiConnection';
import { NoWifiConnection } from './features/ev-charger/wifi-configuration/NoWifiConnection';
import { WifiConfigurationSuccess } from './features/ev-charger/wifi-configuration/WifiConfigurationSuccess';
import { NotificationsArchive } from './features/notifications/NotificationsArchive';
import { NotificationsList } from './features/notifications/NotificationsList';
import { ViewNotification } from './features/notifications/ViewNotification';
import { ConsentError } from './features/site/ConsentError';
import { LocationInfo } from './features/site/new-user-journey/location-permission/LocationInfo';
import { LocationRequiredError } from './features/site/new-user-journey/location-permission/LocationRequiredError';
import { RequireLocationPermission } from './features/site/new-user-journey/location-permission/RequireLocationPermission';
import { SetCustomerSupportConsent } from './features/site/new-user-journey/SetCustomerSupportConsent';
// import { SetupHardware } from './features/site/new-user-journey/SetupHardware';
import { Site } from './features/site/Site';
import { SiteRedirect } from './features/site/SiteRedirect';
import { CreateSiteForm } from './features/sites/CreateSiteForm';
import { ListSites } from './features/sites/ListSites';
import { RedirectToSites } from './features/sites/RedirectToSites';
import { UpdateSiteForm } from './features/sites/UpdateSiteForm';
import { SmartAmpHome } from './features/smart-amp/SmartAmpHome';
import { SmartPlugHome } from './features/smart-plug/SmartPlugHome';
import { ConfirmFlatTariff } from './features/tariff/flat-tariff/ConfirmFlatTariff';
import { FlatTariffRates } from './features/tariff/flat-tariff/FlatTariffRates';
import { EditTariffSeasons } from './features/tariff/tariff-season/EditTariffSeasons';
import { TariffDetails } from './features/tariff/TariffDetails';
import { TariffList } from './features/tariff/TariffList';
import { TariffRoutes } from './features/tariff/TariffRoutes';
import { TariffSetup } from './features/tariff/TariffSetup';
import { TariffSetupComplete } from './features/tariff/TariffSetupComplete';
import { TariffSetupRoutes } from './features/tariff/TariffSetupRoutes';
import { ConfirmTieredTariff } from './features/tariff/tiered-tariff/ConfirmTieredTariff';
import { TieredTariffRates } from './features/tariff/tiered-tariff/TieredTariffRates';
import { ConfirmTOUTariff } from './features/tariff/tou-tariff/ConfirmTOUTariff';
import { TOUTariffRates } from './features/tariff/tou-tariff/TOUTariffRates';

export interface Route {
  path: string;
  element: ReactElement;
  children?: Route[];
  isPublic?: boolean;
}

export const routes: Route[] = [
  {
    element: <SiteRedirect />,
    path: '/',
    isPublic: true,
  },
  {
    element: <Login />,
    path: '/login',
    isPublic: true,
  },
  {
    element: <Logout />,
    path: '/logout',
    isPublic: true,
  },
  {
    element: <UpdateRequired />,
    path: '/update-required',
    isPublic: true,
  },
  {
    element: <RedirectToSites />,
    path: '/site/:id/*',
  },
  {
    path: '/sites',
    element: <Outlet />,
    children: [
      {
        path: '',
        element: <Navigate to="list" />,
      },
      {
        path: 'list',
        element: <ListSites />,
      },
      {
        path: 'create',
        element: <CreateSiteForm />,
      },
      {
        element: <Site />,
        path: ':id',
        children: [
          {
            path: '',
            element: <Navigate replace to="home" />,
          },
          {
            path: 'home',
            element: <EVChargerHome />,
            children: [
              {
                path: 'location_info',
                element: <LocationInfo />,
              },
              {
                path: 'location_required_error',
                element: <LocationRequiredError />,
              },
              {
                path: 'device-register',
                element: (
                  <RequireLocationPermission>
                    <DeviceRegister />
                  </RequireLocationPermission>
                ),
              },
              {
                path: 'device-unplug-ev',
                element: <UnplugEV />,
              },
              {
                path: 'device-power-up-instructions',
                element: <DevicePowerUpInstructions />,
              },
              {
                path: 'device-wait-for-yellow-led',
                element: <WaitForYellowLED />,
              },
              {
                path: 'device-wifi-configuration',
                element: <DeviceWifiConfiguration />,
              },
              {
                path: 'wifi-configuration-success',
                element: <WifiConfigurationSuccess />,
              },
              {
                path: 'device-wifi-2ghz-info',
                element: <Wifi24GhzInfo />,
              },
              {
                path: 'device-wifi-connection',
                element: <DeviceWifiConnection />,
              },
              {
                path: 'no-wifi-connection',
                element: <NoWifiConnection />,
              },
              {
                path: 'device-scan',
                element: <DeviceScan />,
              },
              {
                path: 'wifi-setup-fail',
                element: <WifiSetupFail />,
              },
              {
                path: 'device-details',
                element: <DeviceDetails />,
              },
              {
                path: 'device-status',
                element: <DeviceStatus />,
              },
              {
                path: 'device-not-found',
                element: <NoDevice />,
              },
              {
                path: 'device-removed',
                element: <NoDevice isDeviceRemoved={true} />,
              },
              {
                path: 'device-charging-activity',
                element: <ChargingActivity />,
              },
              {
                path: 'device-charging-schedule',
                element: <DeviceChargingSchedule />,
              },
              {
                path: 'smart-amp',
                element: <SmartAmpHome />,
              },
              {
                path: 'smart-plug',
                element: <SmartPlugHome />,
              },
            ],
          },

          {
            path: 'notifications',
            element: <Outlet />,
            children: [
              {
                path: '',
                element: <Navigate replace to="list" />,
              },
              {
                path: 'list',
                element: <NotificationsList />,
              },
              {
                path: ':notificationId/view',
                element: <ViewNotification />,
              },
              {
                path: 'archive',
                element: <NotificationsArchive />,
              },
            ],
          },
          {
            path: 'account',
            element: <Account />,
            children: [
              {
                path: '',
                element: <Navigate replace to="dashboard" />,
              },
              {
                path: 'dashboard',
                element: <AccountDashboard />,
              },
              {
                path: 'hardware',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="list" />,
                  },
                  {
                    path: 'list',
                    element: <HardwareList />,
                  },
                  {
                    path: ':deviceId',
                    element: <Outlet />,
                    children: [
                      {
                        path: 'view',
                        element: <HardwareDetails />,
                      },
                      {
                        path: 'wifi',
                        element: <HardwareConnection connectionType="WIFI" />,
                      },
                      {
                        path: 'ethernet',
                        element: <HardwareConnection connectionType="ETHERNET" />,
                      },
                    ],
                  },
                  {
                    path: 'configure',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'home',
                        element: <HardwareConfiguration />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'settings',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <SettingsHome />,
                  },
                  {
                    path: 'family-members',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <FamilyMembers />,
                      },
                      // Back-compat redirects for the previously separate view/edit screens.
                      {
                        path: 'view',
                        element: <Navigate replace to="..?redirect=true" />,
                      },
                      {
                        path: 'edit',
                        element: <Navigate replace to="..?redirect=true" />,
                      },
                    ],
                  },
                  {
                    path: 'privacy',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <PrivacySettingsHome />,
                      },
                      {
                        path: 'data-archive',
                        element: <DataArchive />,
                      },
                      {
                        path: 'delete-account',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <DeleteAccountStart />,
                          },
                          {
                            path: 'request-data-archive',
                            element: <DeleteAccountArchive />,
                          },
                          {
                            path: 'verify-details',
                            element: <DeleteAccountVerify />,
                          },
                          {
                            path: 'confirm-deletion',
                            element: <DeleteAccountConfirm />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'display',
                    element: <SettingsDisplay />,
                  },
                  {
                    path: 'language',
                    element: <SettingsLanguage />,
                  },
                ],
              },
              {
                path: 'licenses',
                element: <Outlet />,
                children: [
                  {
                    path: '',
                    element: <LicensesPublishersList />,
                  },
                  {
                    path: ':publisherId',
                    element: <Outlet />,
                    children: [
                      {
                        path: '',
                        element: <LicensesPackagesList />,
                      },
                      {
                        path: ':packageId',
                        element: <LicensesDetail />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'tariff',
                element: <TariffRoutes />,
                children: [
                  {
                    path: '',
                    element: <Navigate replace to="home" />,
                  },
                  {
                    path: 'home',
                    element: <TariffList />,
                  },
                  {
                    path: 'finish',
                    element: <TariffSetupComplete />,
                  },
                  {
                    path: ':tariffId',
                    element: <TariffSetupRoutes />,
                    children: [
                      {
                        path: '',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'season-select',
                        element: <Navigate replace to="home" />,
                      },
                      {
                        path: 'home',
                        element: <TariffSetup />,
                      },
                      {
                        path: 'tariff-details',
                        element: <TariffDetails />,
                      },
                      {
                        path: 'select-season',
                        element: <EditTariffSeasons />,
                      },
                      {
                        path: 'flat',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <FlatTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmFlatTariff />,
                          },
                        ],
                      },
                      {
                        path: 'tou',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <TOUTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmTOUTariff />,
                          },
                        ],
                      },
                      {
                        path: 'tiered',
                        element: <Outlet />,
                        children: [
                          {
                            path: '',
                            element: <Navigate replace to="start" />,
                          },
                          {
                            path: 'start',
                            element: <TieredTariffRates />,
                          },
                          {
                            path: 'confirm',
                            element: <ConfirmTieredTariff />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'support',
                element: <Support />,
              },
            ],
          },
          {
            path: 'update',
            element: <UpdateSiteForm />,
          },
          {
            path: 'set_customer_support_consent',
            element: <SetCustomerSupportConsent />,
          },
        ],
      },
    ],
  },
  {
    element: <ConsentError />,
    path: '/consent-error',
  },
  {
    element: <RouteNotFound />,
    path: '*',
  },
];
